/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Add, Remove } from "@mui/icons-material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Typography,
  InputBase,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { useDispatch } from "react-redux";
import { fetchSupportInformation, fetchFaq } from "../../store/reducers/home/apiThunk";
import { CustomizedShimmer } from "../../components/CommonComponents/Shimmer";
import { ToastNotifyError } from "../../components/CommonComponents/Toast/ToastNotify";
import StyledCustomerSupport from "./StyledCustomerSupport";
import { FAQ_CONSTANTS_MAIN } from "../../constants/lang";

const CustomerSupport = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [supportInformation, setSupportInformation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [faq, setFaq] = useState([]);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  const getSupportInformation = async () => {
    setLoading(true);
    try {
      const res = await dispatch(fetchSupportInformation());
      const { status, message, data } = res?.payload;
      if (status) {
        const buyerSupportInfo = data.find(item => item.support_for === "buyer");
        
        if (buyerSupportInfo) {
          setSupportInformation(buyerSupportInfo);
        } else {
          setSupportInformation(null);
        }
        setLoading(false);
      } else {
        ToastNotifyError(message);
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching support information:", error);
    }
  };
  
  const getFaq = async (searchTerm) => {
    try {
      const res = await dispatch(fetchFaq(searchTerm));
      const { status, message, data } = res?.payload;
      const sellerFaq = data.filter(item => item.section === "buyer");
      if (status === true) {
        setFaq(sellerFaq);
      } else {
        ToastNotifyError(message);
      }
    } catch (error) {
      console.error("Error fetching FAQ:", error);
    }
  };

  useEffect(() => {
    getSupportInformation();
  }, []);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  const highlightText = (text, highlight) => {
    if (!highlight.trim()) {
      return text;
    }
    const regex = new RegExp(`(${highlight})`, "gi");
    const parts = text.split(regex);
    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} style={{ backgroundColor: "#FFDE72", padding: 0 }}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    getFaq(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  return (
    <StyledCustomerSupport>
      {loading ? (
        <Box className='loader-container'>
          <CustomizedShimmer />
        </Box>
      ) : (
        <Box className='main-container'>
          <Box className='header'>
            <ArrowBackIcon
              sx={{ fontSize: "20px", cursor: "pointer" }}
              onClick={() => navigate(-1)}
            />
            <Typography className='header-title'>Customer Support</Typography>
          </Box>

          <Box className='title-box'>
            <Typography component="h1" className='main-title'>
              {FAQ_CONSTANTS_MAIN.maintitle}
            </Typography>

            <Box className='search-box'>
              <Box className='search-container'>
                <SearchIcon className='search-icon' />
                <InputBase
                  className='input-text'
                  placeholder={FAQ_CONSTANTS_MAIN.boxplaceholder}
                  sx={{ flex: 1 }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </Box>
            </Box>

            <Box className='FAQ-container'>
            {faq?.length > 0 ? (
                <Box className='FAQ-content'>
                  <Typography className='FAQ-title'>
                    {FAQ_CONSTANTS_MAIN.FAQtitle}
                  </Typography>
                  <Typography className='FAQ-subtitle'>
                    {FAQ_CONSTANTS_MAIN.FAQsubtitle}
                  </Typography>
                </Box>
              ) : null}

              <Box className='FAQ-box'>
                <Box className='FAQ-content-1'>
                  {faq?.length > 0 ? (
                    faq?.map((item, index) => (
                      <Accordion
                        key={index}
                        expanded={expanded === index}
                        onChange={handleChange(index)}
                        className='FAQ-box-1'
                      >
                        <AccordionSummary
                          expandIcon={
                            expanded === index ? (
                              <IconButton className='IconRemove'>
                                <Remove />
                              </IconButton>
                            ) : (
                              <IconButton className='IconRemove'>
                                <Add />
                              </IconButton>
                            )
                          }
                        >
                          <Typography className='FAQ-main-title'>
                            {highlightText(item?.question, debouncedSearchTerm)}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails className='FAQ-main-subtitle'>
                          <Typography>
                            {highlightText(item?.answer, debouncedSearchTerm)}
                          </Typography>
                        </AccordionDetails>
                      </Accordion>
                    ))
                  ) : (
                    debouncedSearchTerm?.length > 0 ? (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        textAlign: 'center',
                        py: 4,
                        gap: 2
                      }}>
                        <SearchOffIcon sx={{ 
                          fontSize: { xs: '48px', sm: '64px' }, 
                          color: '#9E9E9E',
                          mb: 1
                        }} />
                        <Typography sx={{ 
                          fontSize: { xs: '16px', sm: '18px' },
                          fontWeight: '600',
                          color: '#050505'
                        }}>
                          No Results Found
                        </Typography>
                        <Typography sx={{ 
                          fontSize: { xs: '14px', sm: '16px' },
                          color: '#656565',
                          maxWidth: '400px'
                        }}>
                          Try adjusting your search terms or browse our available FAQs.
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        textAlign: 'center',
                        py: 4,
                        gap: 2
                      }}>
                        <HelpOutlineIcon sx={{ 
                          fontSize: { xs: '48px', sm: '64px' }, 
                          color: '#9E9E9E',
                          mb: 1
                        }} />
                        <Typography sx={{ 
                          fontSize: { xs: '16px', sm: '18px' },
                          fontWeight: '600',
                          color: '#050505'
                        }}>
                          No FAQs Available
                        </Typography>
                        <Typography sx={{ 
                          fontSize: { xs: '14px', sm: '16px' },
                          color: '#656565',
                          maxWidth: '400px'
                        }}>
                          We're working on adding helpful information. Check back soon!
                        </Typography>
                      </Box>
                    )
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          <Box className='contact-container'>
            <Box className='contact-box'>
              <Typography className='contact-title'>
                {FAQ_CONSTANTS_MAIN.contactTitle}
              </Typography>
              <Box className='contact-content'>
                <Box
                  className='contact-subtitle'
                  component='a'
                  href={`tel:${supportInformation?.contact_number}`}
                >
                  <PhoneOutlinedIcon className='PhoneIcon' />
                  <Typography className='phone-title'>
                    {FAQ_CONSTANTS_MAIN.contactPhone}
                  </Typography>
                </Box>

                <Box className='line'></Box>

                <Box
                  className='Mail-box'
                  component='a'
                  href={`mailto:${supportInformation?.email}`}
                >
                  <EmailOutlinedIcon className='EmailIcon' />
                  <Typography className='Email-title'>
                    {FAQ_CONSTANTS_MAIN.contactEmail}
                  </Typography>
                </Box>
              </Box>

              <Typography className='para'>
                {FAQ_CONSTANTS_MAIN.contactpara}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </StyledCustomerSupport>
  );
};

export default CustomerSupport;
